import React from 'react';
import { IconRoute, IconTrendingUp, IconClock, IconBolt, IconFlame, IconChevronRight } from '@tabler/icons-react';
import './SegmentsList.css';

/**
 * Formats seconds into a human-readable time string (e.g., "5:32" or "1:23:45")
 */
const formatTime = (seconds) => {
  if (!seconds) return '-';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats distance in meters to km with 1 decimal
 */
const formatDistance = (meters) => {
  if (!meters) return '-';
  return `${(meters / 1000).toFixed(1)} km`;
};

/**
 * Formats elevation in meters
 */
const formatElevation = (meters) => {
  if (meters === undefined || meters === null) return '-';
  return `${Math.round(meters)} m`;
};

/**
 * Formats average grade as percentage
 */
const formatGrade = (grade) => {
  if (grade === undefined || grade === null) return '-';
  return `${grade.toFixed(1)}%`;
};

/**
 * Formats pace (min/km) from speed (m/s)
 */
const formatPace = (speedMs) => {
  if (!speedMs || speedMs === 0) return '-';
  const paceSecsPerKm = 1000 / speedMs;
  const mins = Math.floor(paceSecsPerKm / 60);
  const secs = Math.round(paceSecsPerKm % 60);
  return `${mins}:${secs.toString().padStart(2, '0')} /km`;
};

/**
 * Formats power in watts
 */
const formatPower = (watts) => {
  if (!watts) return null;
  return `${Math.round(watts)} W`;
};

/**
 * Gets the climb category label
 */
const getClimbCategoryLabel = (category) => {
  if (category === undefined || category === null || category === 0) return null;
  const categories = {
    1: 'Cat 4',
    2: 'Cat 3',
    3: 'Cat 2',
    4: 'Cat 1',
    5: 'HC'
  };
  return categories[category] || null;
};

const SegmentsList = ({
  segments = [],
  selectedSegment,
  onSegmentSelect,
  isLoading = false,
  activityType = 'Ride',
  hideHeader = false
}) => {
  if (isLoading) {
    return (
      <div className="segments-list-loading">
        <div className="segments-loading-spinner"></div>
        <span>Loading segments...</span>
      </div>
    );
  }

  if (!segments || segments.length === 0) {
    return (
      <div className="segments-list-empty">
        <IconRoute size={32} stroke={1.5} />
        <p>No segments on this route</p>
      </div>
    );
  }

  const isRunning = activityType === 'Run' || activityType === 'Trail Run';

  return (
    <div className="segments-list">
      {!hideHeader && (
        <div className="segments-list-header">
          <h3>
            <IconRoute size={18} />
            Segments ({segments.length})
          </h3>
          <p className="segments-list-hint">Select a segment to highlight on map</p>
        </div>
      )}

      <div className="segments-list-items">
        {segments.map((effort) => {
          const segment = effort.segment;
          const isSelected = selectedSegment?.id === effort.id;
          const climbCategory = getClimbCategoryLabel(segment?.climb_category);

          return (
            <div
              key={effort.id}
              className={`segment-item ${isSelected ? 'selected' : ''}`}
              onClick={() => onSegmentSelect(isSelected ? null : effort)}
            >
              <div className="segment-item-header">
                <div className="segment-name-row">
                  <span className="segment-name">{segment?.name || effort.name}</span>
                  {climbCategory && (
                    <span className={`segment-climb-badge climb-${segment.climb_category}`}>
                      {climbCategory}
                    </span>
                  )}
                  {effort.pr_rank && (
                    <span className="segment-pr-badge">PR #{effort.pr_rank}</span>
                  )}
                  {effort.kom_rank && (
                    <span className="segment-kom-badge">KOM #{effort.kom_rank}</span>
                  )}
                </div>
              </div>

              <div className="segment-stats">
                <div className="segment-stat">
                  <IconRoute size={14} />
                  <span>{formatDistance(segment?.distance || effort.distance)}</span>
                </div>

                <div className="segment-stat">
                  <IconTrendingUp size={14} />
                  <span>{formatGrade(segment?.average_grade)}</span>
                </div>

                <div className="segment-stat">
                  <IconTrendingUp size={14} />
                  <span>{formatElevation((segment?.elevation_high || 0) - (segment?.elevation_low || 0))}</span>
                </div>

                <div className="segment-stat">
                  <IconClock size={14} />
                  <span>{formatTime(effort.elapsed_time)}</span>
                </div>

                {effort.average_watts && (
                  <div className="segment-stat power">
                    <IconBolt size={14} />
                    <span>{formatPower(effort.average_watts)}</span>
                  </div>
                )}

                {effort.average_heartrate && (
                  <div className="segment-stat">
                    <IconFlame size={14} />
                    <span>{Math.round(effort.average_heartrate)} bpm</span>
                  </div>
                )}

                {effort.average_cadence && (
                  <div className="segment-stat">
                    <span>{Math.round(effort.average_cadence)} rpm</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SegmentsList;
